import dateFormat from 'dateformat';
import { isEmpty, pickBy } from 'lodash-es';
import { fireValidExistingEmailCustomEvent } from 'client/engagement-handlers/profile-engagement-handler/profile-engagement-handler';
import {
  TRADE_IN_VALUE,
  PRIVATE_PARTY_VALUE,
  DEALER_RETAIL_VALUE,
  CONDITIONS,
  LOCATION_AND_STYLE,
  COLORS_AND_OPTIONS,
  CONDITION_AND_MILEAGE,
  INSTANT_OFFER,
  EMAIL_STEP_TRACKING_ID,
  APPRAISAL_PAGE_CREATIVE_ID,
} from 'site-modules/shared/constants/appraisal/appraisal';
import { APPRAISAL_EMAIL_STEP_SEND_EMAIL } from 'site-modules/shared/constants/appraisal/events';
import { TrackingConstant } from 'client/tracking/constant';
import { asyncValidation } from 'site-modules/shared/components/form-validation/validation-async';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { logger } from 'client/utils/logger/logger';
import { EventToolbox } from 'client/utils/event-toolbox';
import { getQuery } from 'client/utils/location';
import { validateVin } from 'client/site-modules/shared/utils/appraiser/appraiser-validation-helper';
import { rgbToHex } from 'site-modules/shared/utils/color-utils';
import { transformPartnerAppraisalToModsRecord } from 'client/site-modules/shared/utils/multi-offer/multi-offer';

/**
 * Returns saved timestamp in ISO string format.
 * @param {number} daysAfter
 * @returns {string}
 */
export function getSavedTs(daysAfter) {
  const date = new Date();

  if (daysAfter) {
    date.setDate(date.getDate() + daysAfter);
  }

  return date.toISOString();
}

/**
 * @param {number} year
 * @param {string} mileage
 * @returns {number} mileage
 */
export function getMileagePerYear(year, mileage) {
  const currentYear = dateFormat(Date.now(), 'yyyy');
  const yearDiff = currentYear - year;
  return yearDiff > 0 ? parseInt(mileage / yearDiff, 10) : parseInt(mileage, 10);
}

/**
 * @param {array} options
 * @returns {array}
 */
export function collectOptionsData(options) {
  return options.map(({ id, name }) => ({ optionCode: id, optionName: name }));
}

/**
 * Generates appraisalRef by vehicleId.
 * @param {string} vehicleId
 * @returns {string} appraisalRef
 */
export const getAppraisalRef = vehicleId => `#/vehicles/appraisalHistory/${vehicleId}`;

/**
 * Collect data to save to user profile vehicles.
 * @param {object} vehicle
 * @param {string} vehicleId
 * @returns {object} {{vehicles: {}}}
 */
export function collectVehicles({ vehicle }, vehicleId) {
  const {
    style: {
      year,
      name: styleName,
      makeNiceName: niceMake,
      modelNiceName: niceModel,
      submodels: { slug: niceSubModel },
    },
  } = vehicle;

  return {
    vehicles: {
      [vehicleId]: {
        savedTs: getSavedTs(),
        niceMake,
        niceModel,
        niceSubModel,
        styleName,
        year: String(year),
        appraisalRef: getAppraisalRef(vehicleId),
        currentVehicle: true,
      },
    },
  };
}

/**
 * Gets all condition prices by '[PriceType]Value[Condition] template.
 * @param {Object} allConditionTotalPrices.
 * List of all condition prices
 */
function getAllConditionPriceValue(allConditionTotalPrices) {
  const map = {};
  Object.keys(CONDITIONS)
    .filter(key => key !== 'DAMAGED')
    .forEach(key => {
      const condition = CONDITIONS[key];
      const conditionLowerCased = condition.toLowerCase();
      map[`${TRADE_IN_VALUE}Value${condition}`] = allConditionTotalPrices?.[conditionLowerCased]?.[TRADE_IN_VALUE];
      map[`${PRIVATE_PARTY_VALUE}Value${condition}`] =
        allConditionTotalPrices?.[conditionLowerCased]?.[PRIVATE_PARTY_VALUE];
      map[`${DEALER_RETAIL_VALUE}Value${condition}`] =
        allConditionTotalPrices?.[conditionLowerCased]?.[DEALER_RETAIL_VALUE];
    });
  return map;
}

/**
 * Collect data to save to user profile appraisal history.
 * @param {object} vehicle
 * @param {string} condition
 * @param {string} mileage
 * @param {array} options
 * @param {object} color
 * @param {object} totalPrices
 * @param {string} vehicleId
 * @param {number} zipCode
 * @returns {{vehicles: {appraisalHistory: {}, latestAppraisalRef: string}}}
 */
export function collectAppraisalHistory(
  { vehicle, condition, mileage, options, exteriorColor, zipCode, payoffAmount, isPartnerOfferEligibleVin },
  vehicleId,
  totalPrices,
  allConditionTotalPrices,
  offerPrice = null
) {
  const {
    style: {
      year,
      id: styleId,
      name: styleName,
      makeName: make,
      modelName: model,
      /* we are temporarily storing slugs (aka niceIds) in UPS keys named "niceName"
       * as a workaround for a production issue, be aware that niceIds and niceNames
       * have different multi-word string formats -- see EVAL-4003
       */
      makeSlug: appraisalMakeNiceName,
      modelSlug: appraisalModelNiceName,
      submodels: { slug: appraisalSubmodel },
      trimName: appraisalTrim,
    },
  } = vehicle;
  const isOfferPriceEligible = offerPrice === null ? true : offerPrice > 0;
  const isOfferEligibleVin = isPartnerOfferEligibleVin !== false;
  const isOfferEligible = isOfferEligibleVin && isOfferPriceEligible;

  return {
    vehicles: {
      appraisalHistory: {
        [vehicleId]: {
          make,
          model,
          savedTs: getSavedTs(),
          styleId,
          condition,
          options: collectOptionsData(options),
          colors: {
            exterior: exteriorColor?.id,
          },
          styleName,
          isTradeIn: true,
          year: Number(year),
          mileage: Number(mileage),
          appraisalMakeNiceName,
          appraisalModelNiceName,
          appraisalSubmodel,
          appraisalTrim,
          postalCode: zipCode,
          milesPerYear: getMileagePerYear(year, mileage),
          tradeInValue: totalPrices[TRADE_IN_VALUE],
          dealerRetailValue: totalPrices[DEALER_RETAIL_VALUE],
          privatePartyValue: totalPrices[PRIVATE_PARTY_VALUE],
          offerEligibility: `${isOfferEligible}`, // Needs to be string due to Schema.
          offerEligibilityExpiryTs: getSavedTs(isOfferEligible ? 30 : 0),
          payoffAmount,
          ...getAllConditionPriceValue(allConditionTotalPrices),
        },
      },
      latestAppraisalRef: getAppraisalRef(vehicleId),
    },
  };
}

export function collectAppraisalOffersHistory(appraisalOffer) {
  return {
    appraisalOffersHistory: [appraisalOffer],
  };
}

export const VALIDATORS = {
  email: {
    test: validation.validateEmail,
    asyncTest: (params, options) => asyncValidation.fetchEmailValidation({ ...params, id: 'insider' }, options),
    errorText: 'Please enter a valid email address.',
  },
  firstName: {
    test: validation.validateFirstName,
    errorText: 'Please correct your name. Special characters cannot be included.',
  },
  lastName: {
    test: validation.validateName,
    errorText: 'Please correct your name. Special characters cannot be included.',
  },
};

export const sendEmail = async ({ isAuthenticated, doesNotHaveEmail, isValid, email, updateIdmData }) => {
  if (!isValid || !email) {
    return;
  }

  if (isAuthenticated && doesNotHaveEmail) {
    const emailDataToPass = { identifiers: { email: [email] } };
    try {
      await updateIdmData(emailDataToPass);
    } catch (e) {
      logger.warn("Profile data wasn't saved properly", e);
    }
  }

  fireValidExistingEmailCustomEvent(email);
};

export const onEmailStepContinue = async ({ isAuthenticated, doesNotHaveEmail, updateIdmData, email }) => {
  EventToolbox.fireCustomEvent(APPRAISAL_EMAIL_STEP_SEND_EMAIL, { email });

  if (!isAuthenticated || !doesNotHaveEmail || !email) {
    return;
  }

  const emailDataToPass = { identifiers: { email: [email] } };
  await updateIdmData(emailDataToPass);

  EventToolbox.fireTrackAction({
    event_type: TrackingConstant.EVENT_TYPE_ACTION_PROGRESS,
    event_data: {
      action_name: TrackingConstant.ACTION_SIGN_UP,
      subaction_name: TrackingConstant.SUCCESS_EMAIL_ONLY_SIGN_UP,
      action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
      creative_id: EMAIL_STEP_TRACKING_ID,
    },
  });
};

export const getStepCtaAriaLabel = (stepCounter, title, isNextStepLoaded = true) =>
  `${isNextStepLoaded ? 'Continue to' : 'Loading'} step ${+stepCounter + 1}${title ? `: ${title.toLowerCase()}` : ''}`;

export const getStepTitle = (steps = [], stepId) => {
  const step = steps.find(item => item.id === stepId);
  return step ? step.title() : '';
};

/**
 * array of step id that should not have testimonials
 * @type {String[]}
 */
const TESTIMONIALS_INVISIBLE_BY_STEP = [LOCATION_AND_STYLE, COLORS_AND_OPTIONS, CONDITION_AND_MILEAGE];

export const shouldRenderTestimonials = ({ isExtendedReport, activeStepId, isPartnerOfferEligibleVin }) => {
  if (isExtendedReport && !TESTIMONIALS_INVISIBLE_BY_STEP.includes(activeStepId)) {
    return activeStepId === INSTANT_OFFER ? isPartnerOfferEligibleVin : true;
  }
  return false;
};

export const getEmailReportConditions = partnerOfferEmailData => ({
  hasValuation: !!partnerOfferEmailData?.offerValuation,
  hasEstimatedValues: !isEmpty(partnerOfferEmailData?.estimatedValues),
  hasModsId: !!partnerOfferEmailData?.modsId,
});

export const getPredefinedVin = (location, withTracking = false) => {
  const { vin: queryVin } = getQuery(location);
  const isQueryPredefinedVin = !!queryVin;
  const vin = queryVin || '';
  const result = validateVin(vin) ? { vin, isQueryPredefinedVin } : { vin: '', isQueryPredefinedVin: false };
  if (withTracking && result.isQueryPredefinedVin) {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_ACTION_END,
      event_data: {
        action_name: TrackingConstant.ACTION_APPRAISE_VEHICLE,
        action_cause: TrackingConstant.ACTION_CAUSE_USER_INPUT,
        subaction_name: TrackingConstant.VIN_PREPOPULATED,
        creative_id: APPRAISAL_PAGE_CREATIVE_ID,
        action_category: TrackingConstant.USER_ACTION_CATEGORY,
        value: 'vin_in_url',
      },
    });
  }
  return result;
};

export const getTotalPrices = (allConditionTotalPrices, condition) => {
  if (!condition || !allConditionTotalPrices) {
    return {};
  }
  return allConditionTotalPrices[condition.toLowerCase()] || {};
};

/**
 * Transforms the `state.options` where it finds the "cheapest" oemCode and ties that to the option.
 * In simpler terms: 1 option to 1 oem code (cheapest)
 *
 * Used for:
 *  - CarMax's equipment-options
 *  - Multi offer MODS Record creation
 *
 * @returns {Array of Objects}
 */
export const getTransformedOptions = optionsToTransform =>
  optionsToTransform.map(({ name, id, options }) => {
    const cheapestOption = options.reduce(
      ({ oemCode, price: cheapestPrice }, currOption) => {
        const {
          price: { baseMSRP },
          oemCode: currOemCode,
        } = currOption;
        if (baseMSRP < cheapestPrice) {
          return { oemCode: currOemCode, price: baseMSRP };
        }
        return { oemCode, price: cheapestPrice };
      },
      { price: Infinity }
    );
    return {
      id: String(id),
      description: name,
      oemCode: cheapestOption.oemCode,
    };
  });

export const getModsCreationPayload = ({
  vehicleFeatures,
  conditionPricing,
  abTestBucketing,
  dma,
  condition,
  vehicle,
  exteriorColor,
  interiorColor,
  transmissionType,
  engine,
  chromeStyleIds,
  vin,
  mileage,
  zipCode,
  options,
}) => {
  const colors = {};
  const exteriorColorName = exteriorColor?.name ?? '';
  const exteriorColorRgb = exteriorColor?.properties?.rgb ?? '';
  const interiorColorName = interiorColor?.name ?? '';
  const interiorColorRgb = interiorColor?.properties?.rgb ?? '';

  if (!isEmpty(exteriorColor)) {
    colors.exterior = {
      id: String(exteriorColor.id),
      oemName: exteriorColorName,
      hexCode: rgbToHex(...exteriorColorRgb.split(','), false),
      genericName: exteriorColor.genericName || '',
    };
  }

  if (!isEmpty(interiorColor)) {
    colors.interior = {
      id: String(interiorColor.id),
      oemName: interiorColorName,
      hexCode: rgbToHex(...interiorColorRgb.split(','), false),
      genericName: interiorColor.genericName || '',
    };
  }

  const { name = '', size = null, cylinders = 0 } = engine || {}; // Destructured to extract only needed engine properties

  let mileageInt = mileage;
  if (typeof mileage === 'string') {
    mileageInt = parseInt(mileage, 10);
  }

  const modsVehicle = {
    condition,
    year: +vehicle?.style?.year,
    make: vehicle?.make?.name,
    model: vehicle?.model?.name,
    makeSlug: vehicle?.style?.makeSlug,
    modelSlug: vehicle?.style?.modelSlug,
    subModel: vehicle?.style?.submodels?.name,
    subModelSlug: vehicle?.style?.submodels?.slug,
    styleName: vehicle?.style?.name,
    edmundsStyleId: vehicle?.style?.id,
    /* (START) Below fields "can" be undefined so it needs to default accordingly */
    exteriorColor: exteriorColorName,
    interiorColor: interiorColorName,
    colors,
    chromeStyleId: chromeStyleIds?.['0'] ?? '',
    chromeStyleIdList: chromeStyleIds || [],
    // vehicle.transmissionType is intentionally saved from transmissionTypeCode (canonical format required by MODS/CarMax)
    transmissionType: transmissionType || '',
    engineSize: size,
    engine: { name, size, cylinder: cylinders },
    drive: vehicleFeatures?.driveTrain ?? '',
    selectedOptions: getTransformedOptions(options).map(option => ({
      ...option,
      oemCode: option.oemCode || '', // should be empty string for null or undefined
    })),
    /* (END) Above fields "can" be undefined so it needs to default accordingly */
    tmv: {
      /* Specified explicitly so we know exactly what is in the payload */
      average: conditionPricing.average,
      clean: conditionPricing.clean,
      outstanding: conditionPricing.outstanding,
      rough: conditionPricing.rough,
    },
    vin: vin?.toUpperCase(),
    mileage: mileageInt,
  };

  return transformPartnerAppraisalToModsRecord({
    vehicle: modsVehicle,
    dma,
    zipCode,
    abTestBucketing,
  });
};

export const filterQuestionnaireVerboseResponseByPartner = (partnerQuestionnaireVerboseResponse, targetPartner) =>
  pickBy(partnerQuestionnaireVerboseResponse, ({ partner }) => partner === targetPartner);

/**
 * transmission type priority:
 * 1. User-selected transmission type (highest priority)
 * 2. VIN-decoded transmission type
 * 3. Style-decoded transmission type (fallback)
 */
export const getTransmissionData = ({ vinStyles, transmissionType, isTransmissionTypeSelectedByUser }) => {
  let finalTransmissionType;
  let finalTransmissionSource;

  if (isTransmissionTypeSelectedByUser) {
    finalTransmissionType = transmissionType;
    finalTransmissionSource = 'user';
  } else if (vinStyles?.transmissionType) {
    finalTransmissionType = vinStyles.transmissionType;
    finalTransmissionSource = 'vinDecode';
  } else {
    finalTransmissionType = transmissionType;
    finalTransmissionSource = 'styleDecode';
  }
  return { transmissionType: finalTransmissionType, transmissionSource: finalTransmissionSource };
};
